import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Clock, Users, X, CheckCircle } from 'lucide-react';
import useApiMethods from '../../../lib/useApiMethods';
import {
  getEngageSourceBadge, ENGAGE_SOURCE_LABEL,
} from '../../../utils/engageStageBadge';
import { cn } from '../../../utils/tailwindUtils';
import ReportFilters, { filterByRange } from './ReportFilters';
import { ENGAGE_SOURCE_OPTIONS } from '../../../utils/engageSourceLinks';


export default function NoShowRateReport() {
  const { fetch: apiFetch } = useApiMethods();
  const navigate = useNavigate();
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [preset, setPreset] = useState('all');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => {
    setLoading(true);
    (async () => {
      try {
        const resp = await apiFetch('engage/enquiries/');
        setEnquiries(resp?.results || resp?.data?.results || []);
      } catch (e) {
        setError(e?.response?.data?.message || 'Failed to load report');
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadTick]);

  const filtered = useMemo(() => {
    let f = filterByRange(enquiries, preset, customFrom, customTo, 'created_date');
    if (sourceFilter !== 'all') f = f.filter(e => e.source === sourceFilter);
    return f;
  }, [enquiries, preset, customFrom, customTo, sourceFilter]);

  // No-show: appointment_booked + dropped (skipped their appointment).
  // Consulted / under_treatment / converted → attended.
  const stats = useMemo(() => {
    const booked = filtered.filter(e => ['appointment_booked', 'consulted', 'under_treatment', 'follow_up_required', 'converted', 'dropped', 'lost'].includes(e.status));
    const attended = filtered.filter(e => ['consulted', 'under_treatment', 'follow_up_required', 'converted'].includes(e.status));
    const noShow = filtered.filter(e => e.status === 'dropped' || e.status === 'lost' || e.status === 'appointment_booked');
    const showRate = booked.length ? Math.round(attended.length / booked.length * 100) : 0;
    return { booked, attended, noShow, showRate };
  }, [filtered]);

  // By source
  const bySource = useMemo(() => {
    const map = {};
    for (const e of filtered) {
      const s = e.source || 'other';
      if (!map[s]) map[s] = { source: s, booked: 0, attended: 0, no_show: 0 };
      if (['appointment_booked', 'consulted', 'under_treatment', 'follow_up_required', 'converted', 'dropped', 'lost'].includes(e.status)) map[s].booked += 1;
      if (['consulted', 'under_treatment', 'follow_up_required', 'converted'].includes(e.status)) map[s].attended += 1;
      if (['dropped', 'lost', 'appointment_booked'].includes(e.status)) map[s].no_show += 1;
    }
    return Object.values(map).filter(r => r.booked > 0).sort((a, b) => b.booked - a.booked);
  }, [filtered]);

  const sourceOptions = useMemo(() => [
    { value: 'all', label: 'All sources' },
    ...ENGAGE_SOURCE_OPTIONS.map(o => ({ value: o.value, label: o.label })),
  ], []);

  return (
    <div className="p-4 max-w-[1200px] mx-auto space-y-4">
      <button onClick={() => navigate('/engage/reports')} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800">
        <ChevronLeft className="w-4 h-4" /> Back to Reports
      </button>
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-red-500 to-orange-600 flex items-center justify-center text-white shadow-sm">
          <Clock className="w-5 h-5" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">No-Show Rate</h1>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Booked appointments vs those who actually showed up</p>
        </div>
      </div>

      <ReportFilters
        preset={preset} setPreset={setPreset}
        customFrom={customFrom} setCustomFrom={setCustomFrom}
        customTo={customTo} setCustomTo={setCustomTo}
        onReload={() => setReloadTick(t => t + 1)}
        extras={[{ label: 'Source', value: sourceFilter, onChange: setSourceFilter, options: sourceOptions }]}
      />

      {error && <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-300">{error}</div>}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-white dark:bg-gray-800 rounded-xl p-3 border border-gray-100 dark:border-gray-700"><div className="flex items-center gap-2"><Users className="w-4 h-4 text-blue-500" /><span className="text-[10px] uppercase text-gray-500 font-medium">Booked</span></div><div className="text-xl font-bold mt-1 text-gray-900 dark:text-white">{stats.booked.length}</div></div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-3 border border-gray-100 dark:border-gray-700"><div className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /><span className="text-[10px] uppercase text-gray-500 font-medium">Attended</span></div><div className="text-xl font-bold mt-1 text-green-600 dark:text-green-400">{stats.attended.length}</div></div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-3 border border-gray-100 dark:border-gray-700"><div className="flex items-center gap-2"><X className="w-4 h-4 text-red-500" /><span className="text-[10px] uppercase text-gray-500 font-medium">No-show</span></div><div className="text-xl font-bold mt-1 text-red-600 dark:text-red-400">{stats.noShow.length}</div></div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-3 border border-gray-100 dark:border-gray-700"><div className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-indigo-500" /><span className="text-[10px] uppercase text-gray-500 font-medium">Show rate</span></div><div className="text-xl font-bold mt-1 text-indigo-600 dark:text-indigo-400">{stats.showRate}%</div></div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700"><h2 className="text-sm font-semibold text-gray-900 dark:text-white">By source</h2></div>
        <table className="w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-900/40 text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
            <tr>
              <th className="px-4 py-2 text-left font-medium">Source</th>
              <th className="px-4 py-2 text-right font-medium">Booked</th>
              <th className="px-4 py-2 text-right font-medium">Attended</th>
              <th className="px-4 py-2 text-right font-medium">No-show</th>
              <th className="px-4 py-2 text-right font-medium">Show rate</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {loading ? <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">Loading…</td></tr>
            : bySource.length === 0 ? <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">No booked appointments yet.</td></tr>
            : bySource.map(r => {
              const showPct = r.booked ? Math.round(r.attended / r.booked * 100) : 0;
              return (
                <tr key={r.source} className="hover:bg-gray-50 dark:hover:bg-gray-700/40">
                  <td className="px-4 py-2.5"><span className={cn('inline-block px-2 py-0.5 rounded-full text-[10px] font-medium', getEngageSourceBadge(r.source))}>
                    {ENGAGE_SOURCE_LABEL[r.source] || r.source}
                  </span></td>
                  <td className="px-4 py-2.5 text-right font-medium">{r.booked}</td>
                  <td className="px-4 py-2.5 text-right text-green-600 dark:text-green-400">{r.attended}</td>
                  <td className="px-4 py-2.5 text-right text-red-500">{r.no_show}</td>
                  <td className="px-4 py-2.5 text-right"><span className={cn('inline-block px-2 py-0.5 rounded-full text-[10px] font-medium',
                    showPct >= 70 ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' :
                    showPct >= 40 ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' :
                    'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300')}>{showPct}%</span></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
